<template>
  <div class="layout limit">
    <aside class="layout-sidebar">
      <div class="sidebar-logo">
        <!-- <span class="logo-text">logo</span> -->
      </div>
      <el-menu
        :default-active="activeGroupId"
        class="sidebar-menu"
        :router="false"
        @select="handleMenuSelect"
      >
        <el-menu-item
          v-for="item in visibleMenuList"
          :key="item.id"
          :index="item.groupId"
        >
          <i v-if="item.icon" class="iconfont" :class="item.icon"></i>
          <span slot="title" :class="{'green': item.icon === 'icon-a-461906250', 'red': item.icon === 'icon-a-461904152'}">{{ item.name }}</span>
        </el-menu-item>
      </el-menu>

      <div class="sidebar-bottom">
        <template v-if="isLoggedIn">
          <div class="user-info">
            <el-avatar :size="40" :src="userInfo.headImg" class="user-avatar">
              <i class="el-icon-user-solid"></i>
            </el-avatar>
            <div class="user-detail">
              <p class="user-name">{{ userInfo.name }}</p>
              <p class="user-email">{{ userInfo.phone }}</p>
            </div>
          </div>
          <span class="logout-btn" @click="handleLogout">{{ $t('login.logout') }}</span>
        </template>
        <el-button v-else type="primary" round class="login-btn" @click="showLoginDialog = true">
          {{ $t('common.login') }}
        </el-button>
      </div>
    </aside>

    <div class="layout-main">
      <header class="layout-header" v-if="!isHeaderHidden">
        <div class="header-search">
          <el-input
            v-model="searchTitle"
            clearable
            class="search-input"
            @clear="handleSearchClear"
            @keyup.enter.native="handleSearch"
          >
            <i slot="suffix" class="el-icon-search search-icon" @click="handleSearch"></i>
          </el-input>
        </div>
        <div class="header-right">
          <el-dropdown @command="handleLangChange" trigger="click">
            <span class="lang-switch">
              {{ currentLangLabel }}<i class="el-icon-arrow-down el-icon--right"></i>
            </span>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item :command="item.lang" v-for="item in langList" :key="item.lang">{{ item.language }}</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
      </header>
      <main class="layout-content" :class="{ 'no-header': isHeaderHidden, 'contact-content': isContact }">
        <router-view />
      </main>
    </div>

    <login-dialog
      :visible.sync="showLoginDialog"
      @go-register="showRegisterDialog = true"
      @forgot="showForgotDialog = true"
    />
    <register-dialog
      :visible.sync="showRegisterDialog"
      @register="handleRegister"
      @get-code="handleGetCode"
    />
    <forgot-password-dialog
      :visible.sync="showForgotDialog"
      @submit="handleForgotSubmit"
      @get-code="handleGetCode"
    />
  </div>
</template>

<script>
import { getLangList, register, sendEmail, getCateList } from '@/api/base'
import { getI18nLocale } from '@/lang/config'
import LoginDialog from '@/components/LoginDialog.vue'
import RegisterDialog from '@/components/RegisterDialog.vue'
import ForgotPasswordDialog from '@/components/ForgotPasswordDialog.vue'

const HOME_GROUP_ID = '4589e6fc65354ad8aacfceecaeecc6bc'
const DESIGN_GROUP_ID = '508895589c7d4f68a2826b3e0e8544f7'
const PUBLISH_GROUP_ID = 'ee17c4e2cd754aefbc08953739a8f122'
const MINE_GROUP_ID = 'bf1215f75a8d4076907043342535ba6b'
const CONTACT_GROUP_ID = '4cb49af40a0747529b99ef2c98c3c9c9'
const ACTIVE_GROUP_KEY = 'active_group_id'

/**
 * 外链菜单前端约定
 * key 为菜单项 groupId，value 为要打开的 URL
 * 点击外链菜单时新开浏览器 tab，不改变当前选中态
 */
const EXTERNAL_LINKS = {
  [DESIGN_GROUP_ID]: 'https://windowcc.com/home'
}

export default {
  name: 'Layout',
  components: { LoginDialog, RegisterDialog, ForgotPasswordDialog },
  data() {
    return {
      langList: [],
      menuList: [],
      searchTitle: '',
      activeGroupId: sessionStorage.getItem(ACTIVE_GROUP_KEY) || HOME_GROUP_ID,
      showLoginDialog: false,
      showRegisterDialog: false,
      showForgotDialog: false
    }
  },
  computed: {
    isMine() {
      return this.$route.path === '/mine'
    },
    isContact() {
      return this.$route.path === '/contact'
    },
    isHeaderHidden() {
      return this.isMine || this.isContact
    },
    isLoggedIn() {
      return this.$store.getters.isLoggedIn
    },
    userInfo() {
      return this.$store.getters.userInfo || {}
    },
    isAdmin() {
      return this.userInfo.isAdmin === true
    },
    visibleMenuList() {
      return this.menuList.filter(item => this.canViewMenu(item))
    },
    currentLangLabel() {
      const lang = this.$store.getters.language
      const found = this.langList.find(item => item.lang === lang)
      return found ? found.language : (lang === 'zh-CN' ? '简体中文' : 'English')
    },
    currentLanguage() {
      return this.$store.getters.language
    }
  },
  watch: {
    currentLanguage() {
      this.getMenuList()
    },
    isLoggedIn(val) {
      if (!val) {
        this.handleAuthMenuHidden()
      }
    },
    isAdmin() {
      this.handleAuthMenuHidden()
    }
  },
  mounted() {
    this.getLangListFn()
    this.getMenuList()
    this.$root.$on('show-login', () => {
      this.showLoginDialog = true
    })
  },
  beforeDestroy() {
    this.$root.$off('show-login')
  },
  methods: {
    getLangListFn() {
      getLangList().then(res => {
        this.langList = res.data
      }).catch(err => {})
    },
    getMenuList() {
      getCateList().then(res => {
        this.menuList = Array.isArray(res.data) ? res.data : []
        this.handleAuthMenuHidden()
        const defaultItem = this.menuList.find(item => item.groupId === this.activeGroupId)
        if (defaultItem && !this.isAuthMenu(defaultItem)) {
          this.$store.dispatch('setActiveCateId', defaultItem.id)
        }
      }).catch(() => {})
    },
    isAuthMenu(item) {
      return this.isPublishMenu(item) || this.isMineMenu(item)
    },
    canViewMenu(item) {
      if (!this.isLoggedIn && this.isAuthMenu(item)) return false
      if (this.isPublishMenu(item) && !this.isAdmin) return false
      return true
    },
    isPublishMenu(item) {
      return !!item && item.groupId === PUBLISH_GROUP_ID
    },
    isMineMenu(item) {
      return !!item && item.groupId === MINE_GROUP_ID
    },
    isContactMenu(item) {
      return !!item && item.groupId === CONTACT_GROUP_ID
    },
    handleAuthMenuHidden() {
      const activeItem = this.menuList.find(item => item.groupId === this.activeGroupId)
      if (activeItem && !this.canViewMenu(activeItem)) {
        this.activeGroupId = HOME_GROUP_ID
        sessionStorage.removeItem(ACTIVE_GROUP_KEY)
      }
      if (!this.isLoggedIn && this.$route.path === '/mine') {
        this.$router.push('/home')
      }
    },

    handleMenuSelect(groupId) {
      const clickedItem = this.menuList.find(item => item.groupId === groupId)
      const externalUrl = EXTERNAL_LINKS[groupId]
      if (externalUrl) {
        window.open(externalUrl, '_blank')
        return
      }
      if (this.isMineMenu(clickedItem)) {
        if (this.$route.path !== '/mine') {
          this.$router.push('/mine')
        }
        return
      }
      if (clickedItem && this.isContactMenu(clickedItem)) {
        this.activeGroupId = groupId
        sessionStorage.setItem(ACTIVE_GROUP_KEY, groupId)
        if (this.$route.path !== '/contact') {
          this.$router.push('/contact')
        }
        return
      }
      if (this.isPublishMenu(clickedItem)) {
        if (!this.isLoggedIn) {
          this.showLoginDialog = true
          return
        }
        if (!this.isAdmin) {
          this.handleAuthMenuHidden()
          return
        }
        const routeData = this.$router.resolve('/publish/video')
        window.open(routeData.href, '_blank')
        return
      }
      this.activeGroupId = groupId
      sessionStorage.setItem(ACTIVE_GROUP_KEY, groupId)
      const cateId = clickedItem ? clickedItem.id : ''
      this.$store.dispatch('setActiveCateId', cateId)
      if (this.$route.path !== '/home') {
        this.$router.push('/home')
      }
    },
    handleLangChange(lang) {
      this.$store.dispatch('setLanguage', lang)
      this.$i18n.locale = getI18nLocale(lang)
    },
    handleSearch() {
      const title = this.searchTitle.trim()
      const emitSearch = () => {
        this.$root.$emit('home-search', title)
      }
      if (this.$route.path !== '/home') {
        this.$router.push('/home').then(() => {
          this.$nextTick(emitSearch)
        }).catch(() => {
          this.$nextTick(emitSearch)
        })
      } else {
        emitSearch()
      }
    },
    handleSearchClear() {
      this.searchTitle = ''
      this.handleSearch()
    },
    handleRegister(form) {
      register({
        email: form.email,
        emailCode: form.code,
        headImg: form.avatar,
        name: form.nickname,
        password: form.password,
        validatePswd: form.confirmPassword
      }).then(() => {
        this.showRegisterDialog = false
        this.showLoginDialog = true
      }).catch(err => {})
    },
    handleForgotSubmit(form) {
      console.log('forgot password submit', form)
    },
    handleGetCode(email) {
      if (!email) return
      sendEmail(email).then(() => {
        // this.$message.success('验证码已发送')
      }).catch(() => {
      })
    },
    handleLogout() {
      this.$store.dispatch('logout')
      this.handleAuthMenuHidden()
    }
  }
}
</script>

<style lang="scss" scoped>
.layout {
  width: 100%;
  height: 100vh;
  margin: 0 auto;
  display: flex;
  overflow: hidden;
}

.layout-sidebar {
  width: 200px;
  height: 100vh;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  padding: 10px 0;
  background: #FAFAFA;
}

.sidebar-logo {
  padding: 16px 24px;

  .logo-text {
    display: inline-block;
    padding: 4px 16px;
    border: 2px solid #0866FF;
    border-radius: 6px;
    color: #0866FF;
    font-weight: bold;
    font-size: 16px;
  }
}

.sidebar-menu {
  border-right: none;
  flex: 1;
  background: transparent;

  .green {
    color: #63724B;
  }
  
  .red {
    color: #ED3C33;
  }
}

.sidebar-bottom {
  padding: 16px 20px;

  .login-btn {
    width: 100%;
    background: #0866FF;
    border-color: #0866FF;
    font-size: 15px;
    padding: 12px 0;
  }
}

.user-info {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;

  .user-avatar {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    overflow: hidden;
    flex-shrink: 0;
    background: #ddd;
    display: flex;
    align-items: center;
    justify-content: center;
    i {
      font-size: 30px;
      color: #999;
    }
  }

  .user-detail {
    min-width: 0;

    .user-name {
      font-size: 14px;
      font-weight: 600;
      color: #333;
      margin-bottom: 2px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .user-email {
      font-size: 11px;
      color: #999;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}

.logout-btn {
  display: block;
  font-size: 13px;
  color: #999;
  cursor: pointer;
  padding: 4px 0;

  &:hover {
    color: #333;
  }
}

.layout-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
}

.layout-header {
  display: flex;
  align-items: center;
  min-height: 80px;
  padding: 20px 30px;
  position: relative;
  flex-shrink: 0;
}

.header-search {
  position: fixed;
  top: 20px;
  height: 40px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;

  .search-input {
    ::v-deep .el-input__inner {
      border-radius: 20px;
      background: #f5f5f5;
      border: none;
      height: 40px;
    }

    ::v-deep .el-input__suffix {
      right: 6px;
      display: flex;
      align-items: center;

      .el-input__suffix-inner {
        display: flex;
        align-items: center;
        flex-direction: row-reverse;
        gap: 8px;
      }

      .el-input__clear {
        margin: 0;
      }

      .search-icon {
        width: 28px;
        height: 28px;
        line-height: 28px;
        text-align: center;
        background: #0866FF;
        color: #fff;
        border-radius: 50%;
        font-size: 18px;
        cursor: pointer;
      }
    }
  }
}

.header-right {
  margin-left: auto;

  .lang-switch {
    cursor: pointer;
    font-size: 14px;
    color: #333;
    display: flex;
    align-items: center;

    &:hover {
      color: #0866FF;
    }
  }
}

.layout-content {
  flex: 1;
  overflow-y: auto;
  padding: 0 20px 20px;

  &.no-header {
    padding-top: 0;
  }

  &.contact-content {
    padding: 0;
  }
}

@media screen and (min-width: 1728px) {
  .header-search {
    width: calc(32px + calc((1728px - 7 * 32px) / 6 * 2));
  }
  .layout-sidebar {
    width: calc(16px + calc((1728px - 7 * 32px) / 6 * 1));
  }
}

@media screen and (min-width: 1424px) and (max-width: 1727.98px) {
  .header-search {
    width: calc(32px + calc((100vw - 7 * 32px) / 6 * 2));
  }
  .layout-sidebar {
    width: calc(16px + calc((100vw - 7 * 32px) / 6 * 1));
  }
}

@media screen and (min-width: 1192px) and (max-width: 1423.98px) {
  .header-search {
    width: calc(24px + calc((100vw - 6 * 24px) / 5 * 2));
  }
  .layout-sidebar {
    width: calc(12px + calc((100vw - 6 * 24px) / 5 * 1));
  }
}

@media screen and (min-width: 960px) and (max-width: 1191.98px) {
  .header-search {
    width: calc(24px + calc((100vw - 5 * 24px) / 4 * 2));
  }
  .layout-sidebar {
    width: calc(12px + calc((100vw - 5 * 24px) / 4 * 1));
  }
}

@media screen and (min-width: 696px) and (max-width: 959.98px) {
  .header-search {
    width: calc(24px + calc((100vw - 11 * 24px) / 3 * 2));
  }
}
</style>

<style lang="scss">
.v-modal {
  opacity: 0.3 !important;
}

.sidebar-menu.el-menu {
  .el-menu-item {
    height: auto;
    min-height: 48px;
    line-height: 1.35;
    margin: 4px 12px;
    padding-top: 12px;
    padding-bottom: 12px;
    border-radius: 24px;
    font-size: 16px;
    color: #333333 !important;
    font-family: 'Alibaba PuHuiTi 2.0', 'Alibaba PuHuiTi 20', sans-serif;
    display: flex;
    align-items: flex-start;
    white-space: normal;

    i {
      margin-right: 10px;
      margin-top: 1px;
      font-size: 20px;
      line-height: 1;
      color: #333333 !important;
      flex-shrink: 0;
    }

    span {
      min-width: 0;
      white-space: normal;
      word-break: break-word;
    }

    &.is-active {
      background-color: #F2F2F2 !important;
      color: #333333 !important;
    }

    &:hover {
      background-color: #F2F2F2 !important;
      color: #333333 !important;
    }
  }
}
</style>
